import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@cast/infrastructure-api';

import { QueryOptions } from '@cast/shared-types';
import { KentFeature } from '@cast/types-kent';
import { mapKentFeatureDtoToKentFeature } from 'features/kent/utils';
import { useCurrentOrganizationId } from '@cast/infrastructure-organization';
import { useRef } from 'react';

const KENT_CLUSTER_FEATURE_QUERY_KEY = 'kent-cluster-feature';

const getClusterFeatureQueryKey = (params: Params) => [
  KENT_CLUSTER_FEATURE_QUERY_KEY,
  params,
];

type Params = {
  clusterId: string;
  featureId: string;
};

type GetClusterFeatureQueryArgs = {
  params: Params;
  options?: QueryOptions<KentFeature>;
};

export const useGetClusterFeatureQuery = ({
  params,
  options,
}: GetClusterFeatureQueryArgs) => {
  const currentOrganizationId = useCurrentOrganizationId();
  const invalidateServerMetricsCache = useRef(false);

  const query = useQuery({
    queryKey: getClusterFeatureQueryKey(params),
    queryFn: async () => {
      const { data } = await apiClient.kent.getClusterFeature({
        organizationId: currentOrganizationId,
        clusterId: params.clusterId,
        id: params.featureId,
        invalidateCache: invalidateServerMetricsCache.current,
      });
      invalidateServerMetricsCache.current = false;
      return mapKentFeatureDtoToKentFeature(data);
    },
    ...options,
  });

  return {
    ...query,
    refetchWithServerMetricsCacheInvalidation: () => {
      invalidateServerMetricsCache.current = true;
      return query.refetch();
    },
  };
};
